import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon } from "lucide-react";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/Bits";
import { ScreenshotDialog } from "@/components/admin/PaymentsAdmin";

export default function HigherPaymentsAdmin({ refreshKey, onChanged }) {
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);
  useEffect(() => {
    api.get("/higher/payments").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [refreshKey]);
  const pending = rows.filter((p) => p.status === "pending").length;

  const review = async (p, status, admin_note) => {
    try {
      await api.patch(`/higher/payments/${p.id}`, { status, admin_note });
      toast.success(`EMS payment ${status}`);
      setViewing(null);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <section className="mt-12 scroll-mt-24" id="ems-payments" data-testid="higher-payments-admin">
      <h2 className="font-display text-base md:text-lg font-bold">
        EMS Payments {pending > 0 && <span data-testid="higher-payments-pending-count" className="ml-1 rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-zinc-950 align-middle">{pending} to review</span>}
      </h2>
      <p className="text-xs text-zinc-500 mt-1">Admin only. Approve a proof to mark that joiner's EMS as paid.</p>
      <div className="mt-3 space-y-2">
        {rows.length === 0 && <div data-testid="higher-payments-empty" className="rounded-xl border border-dashed border-zinc-800 px-4 py-5 text-sm text-zinc-500">No EMS payments yet.</div>}
        {rows.map((p) => (
          <div key={p.id} data-testid={`higher-payment-row-${p.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl border border-white/5 bg-zinc-900/60 px-4 py-3">
            <div className="min-w-0 flex-1 basis-full sm:basis-auto">
              <div className="font-semibold truncate">{p.joiner_name}</div>
              <div className="text-xs text-zinc-500 truncate">{p.methods.join(", ")}{p.reference ? ` · Ref ${p.reference}` : ""} · {fmtDate(p.created_at)}</div>
            </div>
            <span className="font-mono text-sm">{money(p.amount)}</span>
            <StatusBadge status={p.status} />
            <button type="button" data-testid={`higher-payment-view-${p.id}`} onClick={() => setViewing(p)}
              className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 transition-colors hover:border-sky-400/60 hover:text-white">
              <ImageIcon className="h-3.5 w-3.5" /> {p.status === "pending" ? "Review" : "View"}
            </button>
          </div>
        ))}
      </div>
      {viewing && <ScreenshotDialog payment={viewing} src={`/higher/payments/${viewing.id}/screenshot`} onClose={() => setViewing(null)} onReview={review} />}
    </section>
  );
}
